// After registering, and whenever the registrant comes back: the code(s), live hand-off and badge
// status, and the changes they may make themselves (handoff rules: hotel names until the cutoff,
// flights never renamed, cancellations go to the events team).
import React, { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { usePoll } from "../lib/usePoll.js";
import { useConfirm } from "./ConfirmDialog.jsx";

// The registrant cares whether it's booked; the agent's screen keeps the hand-off detail.
const label = {
  booked: ["Booked", "ok"], notBooked: ["Not booked yet", ""], canceled: ["Canceled", "bad"],
  queued: ["Sending…", ""], confirmed: ["Badge confirmed", "ok"], failed: ["Badge failed · events team notified", "bad"],
};
function Status({ s }) { const [text, tone] = label[s] ?? [s, ""]; return <span className={`status ${tone}`}>{text}</span>; }
const roomState = (r) => !r.travellers.length ? "canceled" : r.agentStatus === "used" ? "booked" : "notBooked";

export default function Done({ registration }) {
  const [reg, setReg] = useState(registration);
  const [editing, setEditing] = useState(null); // traveler id
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dialog, ask] = useConfirm();

  // Codes are with the agent as issued (no hand-off to wait for); only badges are pushed somewhere.
  const settled = reg.badges.every(b => b.perensoStatus !== "queued");
  const refresh = async () => { try { setReg(await api(`/registrations/${reg.id}`)); } catch { /* keep last */ } };
  // The POST response has no traveler ids yet. While a badge is queued, usePoll's first load fetches it instead.
  useEffect(() => { if (!reg.changes && settled) refresh(); }, []);
  usePoll(refresh, 3000, !settled, reg.id);

  const ch = reg.changes ?? {};
  const canEditNames = ch.namesOpen && !ch.flightLocked;
  // The registrant books every seat (spec §9a): one call to the agency per code, until every code is booked.
  const live = reg.rooms.filter(r => roomState(r) !== "canceled");
  const allCanceled = reg.rooms.length > 0 && !live.length;
  const allBooked = live.length > 0 && live.every(r => roomState(r) === "booked");
  const agency = reg.agency?.name || "the travel agent";
  const Agency = agency[0].toUpperCase() + agency.slice(1);
  const phone = reg.agency?.phone;
  const dial = (phone ?? "").split(/x|ext/i)[0].replace(/[^\d+]/g, "");   // what the phone dials: no extension, no punctuation
  const canCall = dial.replace(/\D/g, "").length >= 7;
  // The registrant canceled their own seat but others are still going: they still book, for the others (decided 2026-11-02).
  // Only from the full view: right after registering, travelers are bare names and say nothing about who's the registrant.
  const forOthers = Boolean(reg.changes) && live.length > 0 && !reg.rooms.some(r => r.travellers.some(t => t.registrant));
  const whose = forOthers ? "their" : "your";
  const what = reg.travelMethod === "B" ? `${whose} flights and room` : `${whose} room`;
  const day = (d) => new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric" });
  const callAgency = `call ${agency}${phone ? ` at ${phone}` : ""}`;
  // What happened to each canceled traveler's booking (Task 7b): the registrant only acts on "call".
  const outcomeLine = (x) => ({
    "not-booked": "Nothing had been booked for them.",
    "with-agent": `${Agency} has been told and will cancel their booking. You don't need to call.`,
    done: `${Agency} canceled their booking on ${day(x.at)}.`,
    call: `${Agency} needs more from you: ${callAgency}.${x.note ? ` They said: "${x.note}"` : ""}`,
  }[x.outcome]);
  const toCall = reg.rooms.filter(r => r.removed?.some(x => x.outcome === "call"));

  function startEdit(t) { setEditing(t.id); setForm({ name: t.name, email: t.email, phone: t.phone ?? "" }); setError(null); setNotice(null); }
  async function save(t) {
    setBusy(true); setError(null);
    try {
      const body = {};
      if (canEditNames && form.name.trim() !== t.name) body.name = form.name.trim();
      if (ch.namesOpen && form.email.trim() !== t.email) body.email = form.email.trim();
      if ((form.phone ?? "") !== (t.phone ?? "")) body.phone = form.phone.trim() || null;
      if (!Object.keys(body).length) { setEditing(null); return; }
      setReg(await api(`/registrations/${reg.id}/travellers/${t.id}`, { method: "PUT", body }));
      setEditing(null);
      setNotice(body.name ? `Name changed; no new code is needed. ${reg.agency?.name || "The travel agent"} sees the new name${reg.badgeMode === "none" ? "" : ", and the badge is being updated"}.` : "Saved.");
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }
  async function cancel(t) {
    const answer = await ask({ title: `Ask the events team to cancel ${t.name}'s seat?`, body: "They decide, and you'll see the outcome here.", note: "Why? (optional)", confirmLabel: "Send to the events team", cancelLabel: "Go back" });
    if (!answer) return;
    const reason = answer.note;
    setBusy(true); setError(null);
    try { setReg(await api(`/registrations/${reg.id}/travellers/${t.id}/cancel`, { method: "POST", body: { reason } })); setNotice("Sent to the events team. They will decide and get back to you."); }
    catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  return (
    <>
      <h1>{allCanceled ? "Your trip is canceled." : allBooked ? (forOthers ? "Everyone's booked." : "You're booked.")
        : forOthers ? "One call left to book the trip for the others." : "You're registered. One call left to book your trip."}</h1>
      {allCanceled
        ? <p className="lead">Everyone on your registration has been canceled. If that isn't right, contact the events team at foodshow@liparifoods.com.</p>
        : <ol className="steps" role="list">
            <li className="done"><span className="tick" aria-hidden="true">✓</span><span><b>Registered</b><small>Done. Your code{live.length > 1 ? "s are" : " is"} below.</small></span></li>
            <li className={allBooked ? "done" : "todo"}><span className="tick" aria-hidden="true">{allBooked ? "✓" : "2"}</span>
              {allBooked
                ? <span><b>Booked</b><small>{Agency} has booked {what}.</small></span>
                : <span><b>Book {forOthers ? "the" : "your"} trip: call {agency} with {live.filter(r => roomState(r) !== "booked").length > 1 ? "each code" : forOthers ? "the code" : "your code"}</b><small>They book {what}. Lipari pays, but nothing is booked until you call.{canCall ? "" : " The events team can give you the number: foodshow@liparifoods.com."}</small></span>}
            </li>
          </ol>}
      {toCall.length > 0 && <div className="notice warn" role="status"><b>{Agency} needs more from you about {toCall.map(r => r.code).join(" and ")}.</b> Please {callAgency} with your code.</div>}
      {(toCall.length > 0 || (!allCanceled && !allBooked)) && canCall && <a className="call" href={`tel:${dial}`}>Call {agency} · <span className="nowrap">{phone}</span></a>}
      {notice && <div className="notice">{notice}</div>}
      {error && <div className="error" role="alert">{error}</div>}
      {dialog}

      {reg.rooms.map(r => (
        <div className="card" key={r.id}>
          <div className="code">{r.code}</div>
          <div className="row"><span>{roomState(r) === "canceled" ? "Everyone on this code was canceled" : "Booking"}</span><Status s={roomState(r)} /></div>
          {r.travellers.map(t => {
            const tr = typeof t === "string" ? { name: t } : t;
            const pending = ch.pendingCancellations?.includes(tr.id);
            return (
              <div className="traveller-line" key={tr.id ?? tr.name}>
                {editing === tr.id ? (
                  <div className="edit">
                    <label>Name<input type="text" value={form.name} disabled={!canEditNames} onChange={e => setForm({ ...form, name: e.target.value })} /></label>
                    {!canEditNames && ch.flightLocked && <div className="help">Flights are non-refundable, so the name on a flight can't change. If {tr.name} can't travel, ask the events team to cancel the seat.</div>}
                    {!canEditNames && !ch.flightLocked && <div className="help">Name changes closed on {ch.cutoffLabel}. Contact the events team.</div>}
                    <label>Email<input type="email" value={form.email} disabled={!ch.namesOpen} onChange={e => setForm({ ...form, email: e.target.value })} /></label>
                    <label>Mobile<input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></label>
                    <div className="edit-actions"><button className="primary" disabled={busy} onClick={() => save(tr)}>{busy ? "Saving…" : "Save"}</button><button className="ghost" onClick={() => setEditing(null)}>Cancel</button></div>
                  </div>
                ) : (
                  <div className="row">
                    <span><b>{tr.name}</b>{tr.email && <><br /><small className="help">{tr.email}{tr.phone ? ` · ${tr.phone}` : ""}</small></>}{pending && <><br /><small className="status">Cancellation requested</small></>}</span>
                    {tr.id && !pending && (
                      <span className="actions">
                        <button className="ghost" onClick={() => startEdit(tr)}>Change</button>
                        <button className="ghost" onClick={() => cancel(tr)}>Can't travel</button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {r.removed?.map((x, i) => (
            <div className="traveller-line removed" key={`removed-${i}`}>
              <div className="row"><span><s>{x.name}</s>, canceled {day(x.canceledAt)}<br /><small className="help">{outcomeLine(x)}</small></span></div>
            </div>
          ))}
        </div>
      ))}
      {ch.cutoffLabel && !allCanceled && <p className="help">{ch.flightLocked ? "Flight names are fixed. " : ""}Hotel names and emails can be changed until {ch.cutoffLabel}. After that, contact the events team.</p>}

      {reg.badgeMode !== "none" && <>
        <h2>Badges · Perenso</h2>
        {reg.badges.length ? (
          <div className="card">
            {reg.badges.map(b => (
              <div className="row" key={b.id}><span>{b.name}<br /><small className="help">{b.email}</small></span><Status s={b.perensoStatus} /></div>
            ))}
          </div>
        ) : <p className="help">No badges on this registration.</p>}
      </>}
    </>
  );
}
